// "All off" for the room — the button you press on the way out.
//
// Asked for 2026-10-26 (the owner): "in room level there should be button to
// all off, which will help to close all lights while leaving room."
//
// ⚠️ IT SAYS WHAT IT WILL DO BEFORE IT DOES IT. A room button that switched off
// four devices on one tap is the kind of thing that catches somebody's AC, a
// light somebody is reading under, or a load that was deliberately left on — so
// it names the count, lists what it will NOT touch, and only then sends. Same
// rule as "Remove room" and Unassign: the count comes before the write.
//
// ⚠️ ONE COMMAND AT A TIME, WITH A GAP. A device drops a command sent right
// behind another — the smart-room sweep found this on real hardware and waits
// 500 ms between publishes. A loop that fired ten at once would leave half a
// room on, which is worse than not pressing the button.
//
// ⚠️ AND IT CHANGES NOTHING ON SCREEN. The tiles recolour when the devices
// echo their status, exactly as everywhere else (useRelayStatus) — an optimistic
// "all off" would lie about a device that never got the message.
//
// What is sent per type, and what is refused, is core/devices/roomAllOff.

import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Checkbox, LinearProgress, Typography,
} from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { useApp } from '../../core/state/AppContext';
import { getVacancyWatches } from '../../core/api/api';
import { planRoomAllOff, allOffSummary } from '../../core/devices/roomAllOff';
import { publishCommand } from '../../core/utils/deviceController';
import { showToast } from './toast';
import { FIT_XS } from './fitDialog';

// The pause the smart-room sweep uses between two publishes to one device.
export const GAP_MS = 500;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * `devices` — the room's device rows.
 *
 * ⚠️ THE KEEP-ON LIST IS READ WHEN THE DIALOG OPENS, NEVER ON MOUNT. It is one
 * request, and a room screen must not spend it on a button nobody pressed —
 * startup traffic is exactly what the owner has been trimming. `keepOn` may
 * also be passed in by a caller that already has it.
 */
export default function RoomAllOffButton({
  devices, keepOn = '', roomId = 0, locationId = 0, roomName = '', sx, variant = 'text',
}) {
  const { userId } = useApp();
  const [open, setOpen] = useState(false);
  const [loadedKeep, setLoadedKeep] = useState(null);   // null = not asked yet
  const [includeAc, setIncludeAc] = useState(false);
  const [sending, setSending] = useState(0);

  // What the caller gave us, else what the room's watch says, else nothing.
  const keep = keepOn || loadedKeep || '';
  const plan = useMemo(
    () => planRoomAllOff(devices, { keepOn: keep, includeAc }),
    [devices, keep, includeAc],
  );
  // Nothing switchable in this room (all sensors, a lone lock) — then there is
  // no button, rather than one that does nothing.
  const anything = useMemo(
    () => planRoomAllOff(devices, { includeAc: true }).counts.messages > 0,
    [devices],
  );
  if (!anything) return null;

  const run = async () => {
    const list = plan.messages;
    setSending(1);
    let sent = 0;
    for (let i = 0; i < list.length; i += 1) {
      /* eslint-disable no-await-in-loop */
      try {
        publishCommand(list[i].uniqueNumber, list[i].message, userId);
        sent += 1;
      } catch (e) { /* a dead client is not an error worth stopping for */ }
      setSending(i + 2);
      if (i < list.length - 1) await sleep(GAP_MS);
      /* eslint-enable no-await-in-loop */
    }
    setSending(0);
    setOpen(false);
    // ⚠️ "SENT", NEVER "SWITCHED OFF". Publishing is not proof: the tiles will
    // recolour when the devices echo, and a device that is offline will not.
    showToast(
      `${sent} command${sent === 1 ? '' : 's'} sent${plan.counts.kept ? ` · ${plan.counts.kept} kept on` : ''}`
        + ' — the buttons change colour as each device answers',
      { severity: 'success' },
    );
  };

  return (
    <>
      <Button
        size="small"
        variant={variant}
        startIcon={<PowerSettingsNewIcon fontSize="small" />}
        onClick={() => {
          setOpen(true);
          // ⚠️ A FAILED READ MUST NOT MEAN "NOTHING IS KEPT ON" silently — the
          // dialog says the list could not be read, so a fridge is not switched
          // off on the strength of a timeout.
          if (loadedKeep == null && !keepOn && roomId && locationId) {
            getVacancyWatches(locationId, userId)
              .then((r) => {
                const rows = Array.isArray(r?.data?.watches) ? r.data.watches
                  : (Array.isArray(r?.data) ? r.data : []);
                const mine = rows.find((w) => Number(w.RoomID) === Number(roomId));
                setLoadedKeep(mine ? String(mine.KeepOn || '') : '');
              })
              .catch(() => setLoadedKeep(false));
          }
        }}
        sx={sx}
      >
        All off
      </Button>

      <Dialog open={open} onClose={sending ? undefined : () => setOpen(false)} {...FIT_XS}>
        <DialogTitle sx={{ pb: 0.5 }}>{`Switch off ${roomName || 'this room'}?`}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" className="fit-text">
            {allOffSummary(plan)}
            .
          </Typography>

          {loadedKeep === false && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Could not read this room’s keep-on list — anything on it may be switched off too.
            </Alert>
          )}

          {!!plan.counts.kept && (
            <Alert severity="info" sx={{ mt: 1 }}>
              {`${plan.counts.kept} output${plan.counts.kept === 1 ? '' : 's'} stay on — they are on this room’s keep-on list.`}
            </Alert>
          )}

          {!!plan.skipped.length && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">Not touched:</Typography>
              {plan.skipped.map((s) => (
                <Typography key={s.uniqueNumber} variant="caption" display="block" color="text.secondary">
                  {`• ${s.name} — ${s.why}`}
                </Typography>
              ))}
            </Box>
          )}

          {/* The blaster's lights go off either way; its AC is a bigger
              decision, so it is asked for, never assumed. */}
          {devices?.some((d) => [19, 57, 62].includes(Number(d.DeviceID))) && (
            <FormControlLabel
              sx={{ mt: 0.5 }}
              control={<Checkbox size="small" checked={includeAc} onChange={(e) => setIncludeAc(e.target.checked)} />}
              label={<Typography variant="body2">Switch the AC off too</Typography>}
            />
          )}

          {!!sending && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {`sending ${Math.min(sending, plan.messages.length)} of ${plan.messages.length}…`}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(Math.min(sending, plan.messages.length) / Math.max(1, plan.messages.length)) * 100}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={!!sending}>Cancel</Button>
          <Button variant="contained" color="error" onClick={run} disabled={!!sending || !plan.messages.length}>
            All off
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
